import { Capacitor } from '@capacitor/core';

/**
 * Single source of truth for "is this client allowed to PLAY the game on
 * the web?"
 *
 * HISTORY: the web shell (pokerblitzgame.com/play and /labs) was gated from
 * the public on 2026-06-02 (#208) because it wasn't production-ready
 * VISUALLY. That reason is now resolved — the board-rendering fixes in
 * #214/#221/#223 (2026-06-03) and #254 (2026-06-11) made the web shell
 * presentable, verified rendering correctly on desktop + mobile on
 * 2026-06-23. Public web play is now ALLOWED so promo traffic can convert
 * (the daily Bluesky funnel now points desktop clicks at /play). This
 * function therefore returns true for everyone; the explicit native / smoke
 * / dev / `?play=1` / `battle` / `admin_flag` branches below are retained
 * (they still persist the sticky override harmlessly) so re-gating is a
 * one-line change if the policy ever reverses. Keep in sync with the route
 * guards in App.tsx — it's the only place that decides.
 */
const OVERRIDE_KEY = 'allow_web_play';

export function isWebPlayAllowed(): boolean {
  // The native app IS the product — never gate it, regardless of how its
  // webview reports env flags.
  if (Capacitor.isNativePlatform()) return true;

  // Playwright smoke suite loads /play directly with this build flag set.
  if (import.meta.env.VITE_SMOKE_TEST === 'true') return true;

  // CI's "Web smoke" job builds with VITE_ALLOW_TEST_SUPABASE_FALLBACK (but
  // NOT VITE_SMOKE_TEST) and navigates straight to /play and /labs. That flag
  // is ONLY ever set for test/smoke builds — real prod builds never set it
  // (see .github/workflows/ci.yml + mobile-prod-deploy.yml) — so it's a safe
  // "this is a test build, don't gate" signal that keeps the smoke specs green.
  if (import.meta.env.VITE_ALLOW_TEST_SUPABASE_FALLBACK === 'true') return true;

  // Local `vite dev` — let developers run the game without the override.
  if (import.meta.env.DEV) return true;

  // SSR / non-browser context: nothing to gate, render normally.
  if (typeof window === 'undefined') return true;

  try {
    const params = new URLSearchParams(window.location.search);
    // Sticky QA bypass: visiting any game URL with `?play=1` once grants
    // web play on this device until localStorage is cleared.
    if (params.get('play') === '1') {
      window.localStorage.setItem(OVERRIDE_KEY, '1');
      return true;
    }
    // Poker Blitz Battle (epic aaq3): a battle link is the public acquisition
    // hook — the friend who taps /play?battle=<code> MUST be able to play the
    // challenge on the web (try-before-install), or the viral loop dead-ends at
    // the download gate. Granting web play here (sticky, like ?play=1) is the
    // deliberate exception to the "web is gated for the public" rule: Battle is
    // the one polished, shareable web entry point. The /b/<code> landing is
    // already public; this lets its "Accept Challenge" CTA actually deliver.
    if (params.get('battle')) {
      window.localStorage.setItem(OVERRIDE_KEY, '1');
      return true;
    }
    if (window.localStorage.getItem(OVERRIDE_KEY) === '1') return true;
    // Admins/beta testers already carry this flag (set for the mobile
    // mirror preview) — let them through too.
    if (window.localStorage.getItem('admin_flag') === 'true') return true;
  } catch {
    /* localStorage blocked (private mode / cookies off) — treat as public */
  }

  // Public web play is now allowed (see HISTORY above). To re-gate the
  // public web shell, change this back to `return false`.
  return true;
}

/** Sticky Echidna web-access grant. Deliberately NOT `allow_web_play`. */
const ECHIDNA_OVERRIDE_KEY = 'echidna.allow_web_access';

/**
 * Is this client allowed to reach Echidna (the WORD game) on the web?
 *
 * WHY THIS IS NOT `isWebPlayAllowed()` (bd StackedStreak-6xm1)
 * -----------------------------------------------------------
 * It used to be. `/echidna` called the same gate as `/play` and `/labs`, with a
 * comment in App.tsx promising the public "shouldn't stumble into it on the
 * production domain". On 2026-06-23, PR #261 flipped that gate to an
 * unconditional `return true` so the Poker Blitz Battle funnel could convert
 * web traffic — a decision that was entirely correct FOR POKER, and had nothing
 * to do with Echidna. Echidna was ungated as a side effect that nobody chose
 * and nobody noticed, and a pre-beta word game sat publicly playable on a live
 * poker domain for five weeks.
 *
 * That is the whole argument for this function existing. Two apps must not
 * share one boolean when only one of them has shipped: the next change to
 * Poker Blitz's acquisition funnel must not be able to publish Echidna again.
 * Per docs/TWO_APPS_ONE_REPO.md, when a change could affect both, protect the
 * one that is live.
 *
 * Echidna's real destination is its own host (bd StackedStreak-era7 /
 * StackedStreak-nkr1.34, Netlify), at which point the `/echidna` route can
 * leave the poker build entirely and this gate can go with it.
 */
export function isEchidnaWebAccessAllowed(): boolean {
  // The native Echidna app (com.echidna.game) IS the product — never gate it.
  if (Capacitor.isNativePlatform()) return true;

  // Playwright: every config (playwright.config, .critical, .audit,
  // .audit-echidna) sets VITE_SMOKE_TEST, and the echidna specs navigate
  // straight to /echidna?wbcapture=1.
  if (import.meta.env.VITE_SMOKE_TEST === 'true') return true;

  // CI's "Web smoke" job builds with this instead. Only ever set for
  // test/smoke builds — real prod builds never set it.
  if (import.meta.env.VITE_ALLOW_TEST_SUPABASE_FALLBACK === 'true') return true;

  // Local `vite dev` — developers work on Echidna without an override.
  if (import.meta.env.DEV) return true;

  // SSR / non-browser context: nothing to gate.
  if (typeof window === 'undefined') return true;

  try {
    // Sticky QA bypass, same ergonomics as poker's `?play=1` but recorded under
    // Echidna's own key, so a tester who unlocked poker's web shell does NOT
    // silently inherit access to an unlaunched game.
    if (new URLSearchParams(window.location.search).get('play') === '1') {
      window.localStorage.setItem(ECHIDNA_OVERRIDE_KEY, '1');
      return true;
    }
    if (window.localStorage.getItem(ECHIDNA_OVERRIDE_KEY) === '1') return true;
    // Admins / beta testers already carry this flag.
    if (window.localStorage.getItem('admin_flag') === 'true') return true;
  } catch {
    // localStorage blocked (private mode / cookies off). Poker treats that as
    // public; Echidna must treat it as NOT public — an unlaunched game fails
    // closed, never open.
    return false;
  }

  // Echidna is pre-beta. The public does not get in. Flip this to `true` on
  // the day the beta opens.
  return false;
}
